import { createElement, forwardRef, lazy } from 'react'

/**
 * Warm-up helpers for `React.lazy` code-split points.
 *
 * Several panels are code-split behind `React.lazy` (the deadline hourglass,
 * the list colour picker, the priority thermometer, the Action sidebar itself).
 * Because the dynamic `import()` only fires when the component first renders,
 * the chunk request sits on the critical path of the interaction that opened
 * the panel - so the animation cannot start until the network round trip *and*
 * module evaluation finish. Measured cold cost: ~1s in dev, hundreds of ms in
 * production. That is the "every animation has a slight delay" symptom.
 *
 * `lazyWithPreload` exposes the memoised factory as `.preload()`, and
 * `preloadOnIdle` kicks it off during idle time once the owning module has been
 * evaluated. By the time the user clicks, the module is already in memory and
 * the lazy component resolves synchronously (React.lazy reuses the very same
 * promise), so the animation starts on the first frame.
 */

const scheduleIdle = (cb) => {
  if (typeof window === 'undefined') return
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(cb, { timeout: 2000 })
  } else {
    // Safari < 17 has no requestIdleCallback
    window.setTimeout(cb, 300)
  }
}

/**
 * Like `React.lazy`, but:
 *
 *  - the returned component has a `.preload()` method that starts (and
 *    memoises) the dynamic import ahead of first render, and
 *  - once the module has been loaded it is rendered *directly*, without
 *    suspending.
 *
 * That second part matters as much as the prefetch. `React.lazy` always
 * suspends on its first render, even when the underlying import has already
 * resolved - and React throttles how quickly a suspended tree may be revealed
 * (~300ms buckets). That throttle is what made every panel animation start "a
 * beat late": measured 344ms from click to the deadline hourglass appearing,
 * versus 63ms for the rest of the panel, with the module already in memory.
 * Rendering the resolved module directly keeps the boundary from suspending at
 * all, so the animation starts on the same commit as the panel.
 */
export const lazyWithPreload = (factory) => {
  let promise
  let loaded

  const load = () => {
    if (!promise) {
      promise = factory().then((mod) => {
        loaded = mod
        return mod
      })
    }
    return promise
  }

  const Lazy = lazy(load)

  const Component = forwardRef((props, ref) => {
    if (loaded) {
      // Already in memory: render synchronously, no Suspense reveal throttle.
      const Resolved = loaded.default ?? loaded
      return createElement(Resolved, { ...props, ref })
    }
    // Not loaded yet - behave exactly like React.lazy (suspend, show fallback).
    return createElement(Lazy, { ...props, ref })
  })

  Component.displayName = 'LazyWithPreload'
  Component.preload = load
  return Component
}

/**
 * Warm up one or more `lazyWithPreload` components during idle time. Safe to
 * call at module scope; failures are swallowed because React.lazy will surface
 * a real load error at render time via the nearest error boundary.
 */
export const preloadOnIdle = (...components) => {
  scheduleIdle(() => {
    components.forEach((component) => {
      if (typeof component?.preload !== 'function') return
      try {
        const result = component.preload()
        if (result && typeof result.catch === 'function') {
          result.catch(() => {})
        }
      } catch {
        // ignore - retried at render time
      }
    })
  })
}
